import type { GetTotalStaffResponseDto } from '@/shared/api/modules/staff';
import { FocusSummaryCard } from '@/shared/components';
import { useFacilityScope } from '@/shared/lib/hooks/use-facility-scope';
import { formatNumber } from '@/shared/lib/number';
import { useQuery } from '@tanstack/react-query';
import type { Dayjs } from 'dayjs';
import { type FC } from 'react';
import { getTotalStaffQueryOptions } from '../api/get-total-staff.query';

interface TotalStaffCardProps {
  dateRange: {
    from: Dayjs;
    to: Dayjs;
  };
  /**
   * Every page shows this card as "Number of Staff" (per Figma). The Facilities pages pass the
   * title explicitly; the Staff page uses the default.
   */
  title?: string;
}

// TODO:  make this logic for this chart reusable
const sparklineToChartData = (
  sparkline: GetTotalStaffResponseDto['sparkline']
) => {
  const values = sparkline.points.map((p) => p.value);
  const labels = sparkline.points.map((p) => p.end);

  const cleanedValues: number[] = [];
  const cleanedLabels: string[] = [];

  values.forEach((val, i) => {
    if (i === 0 || val !== values[i - 1]) {
      cleanedValues.push(val ?? 0);
      cleanedLabels.push(labels[i]);
    }
  });

  return {
    labels: cleanedLabels,
    datasets: [
      {
        label: sparkline.granularity,
        data: cleanedValues,
        borderColor: '#165BAA',
        backgroundColor: 'rgba(22, 91, 170, 0.2)',
      },
    ],
  };
};

export const TotalStaffCard: FC<TotalStaffCardProps> = ({
  dateRange,
  title = 'Number of Staff',
}) => {
  const { scopedFacilityId } = useFacilityScope();
  const { data, isLoading } = useQuery(
    getTotalStaffQueryOptions({
      from: dateRange.from.format('YYYY-MM-DD'),
      to: dateRange.to.format('YYYY-MM-DD'),
      facilitiesIds: scopedFacilityId ? [scopedFacilityId] : undefined,
    })
  );

  const sparklineData = data?.sparkline
    ? { data: sparklineToChartData(data?.sparkline) }
    : undefined;

  const indicator =
    data?.changePct != null
      ? {
          value: data.changePct,
          negativeValue: data.changePct < 0,
        }
      : undefined;

  return (
    <FocusSummaryCard
      icon="team"
      tooltipMessage="Average number of staff members during the selected period"
      title={title}
      chart={sparklineData}
      indicator={indicator}
      value={data?.totalStaff ? `${formatNumber(data?.totalStaff)}` : 'N/A'}
      loading={isLoading}
    />
  );
};
